
// ABOUTME: Hook for security audit logging and monitoring
// ABOUTME: Tracks security events and provides audit trail functionality

import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useUserRoles } from './useUserRoles';
import { logger } from '@/utils/sentry';

interface SecurityAuditLog {
  id: string;
  user_id: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  error_message?: string;
  metadata?: any;
  created_at: string;
}

interface SecurityAuditEvent {
  action: string;
  resource_type: string;
  resource_id?: string;
  ip_address?: string;
  user_agent?: string;
  success: boolean;
  error_message?: string;
  metadata?: any;
}

export function useSecurityAudit() {
  const { user } = useAuth();
  const { canViewAuditLogs } = useUserRoles();

  // Fetch audit logs (admin only) - gracefully handle missing table
  const { data: auditLogs, isLoading } = useQuery({
    queryKey: ['security-audit-logs'],
    queryFn: async (): Promise<SecurityAuditLog[]> => {
      if (!canViewAuditLogs()) {
        throw new Error('Insufficient permissions');
      }

      try {
        const { data, error } = await supabase
          .from('security_audit_log')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) {
          logger.warn("Failed to fetch audit logs", { error: error.message });
          return [];
        }

        return data || [];
      } catch (error) {
        logger.warn("Failed to fetch audit logs", { error });
        return [];
      }
    },
    enabled: canViewAuditLogs(),
  });

  // Log security event - gracefully handle missing table
  const logSecurityEvent = useMutation({
    mutationFn: async (event: SecurityAuditEvent) => {
      if (!user?.id) return;

      const auditData = {
        user_id: user.id,
        action: event.action,
        resource_type: event.resource_type,
        resource_id: event.resource_id,
        ip_address: event.ip_address,
        user_agent: event.user_agent || navigator.userAgent,
        success: event.success,
        error_message: event.error_message,
        metadata: event.metadata || {},
      };

      try {
        const { error } = await supabase
          .from('security_audit_log')
          .insert(auditData);

        if (error) {
          logger.error("Failed to log security event to database", { 
            error: error.message,
            event: event.action 
          });
          // Fallback to console logging
          console.log("Security event (DB insert failed):", auditData);
        } else {
          logger.info("Security event logged", { 
            action: event.action,
            success: event.success 
          });
        }
      } catch (error) {
        logger.error("Failed to log security event", { 
          error: error instanceof Error ? error.message : 'Unknown error',
          event: event.action 
        });
        // Fallback to console logging
        console.log("Security event (exception):", auditData);
      }
    },
  });

  // Helper functions for common security events
  const logLoginAttempt = (success: boolean, errorMessage?: string) => {
    logSecurityEvent.mutate({
      action: 'login_attempt',
      resource_type: 'auth',
      success,
      error_message: errorMessage,
    });
  };

  const logReferralCreation = (referralId: string, success: boolean, errorMessage?: string) => {
    logSecurityEvent.mutate({
      action: 'referral_creation',
      resource_type: 'referral',
      resource_id: referralId,
      success,
      error_message: errorMessage,
    });
  };

  const logProfileUpdate = (success: boolean, errorMessage?: string) => {
    logSecurityEvent.mutate({
      action: 'profile_update',
      resource_type: 'profile',
      resource_id: user?.id,
      success,
      error_message: errorMessage,
    });
  };

  const logPermissionDenied = (action: string, resourceType: string) => {
    logSecurityEvent.mutate({
      action: 'permission_denied',
      resource_type: resourceType,
      success: false,
      error_message: `Access denied for action: ${action}`,
    });
  };

  return {
    auditLogs: auditLogs || [],
    isLoading,
    logSecurityEvent: logSecurityEvent.mutate,
    logLoginAttempt,
    logReferralCreation,
    logProfileUpdate,
    logPermissionDenied,
  };
}
